import { useAuth0 } from "@auth0/auth0-react";
import React from "react";

import { Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { useUpdateProviderProfileMutation } from "@api/graphql/types-and-hooks";
import { useFormUtils } from "@components/forms/useFormUtils";
import { OnboardingShell } from "@pages/Onboarding/OnboardingShell";
import { useAdminData } from "@providers/AdminDataProvider";

interface OnboardingProfileForm {
  organization: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface GetToKnowYouStepProps {
  // Advances to the next step, handing the entered name up so the parent can keep
  // it as a draft (and re-show it if the user navigates back to this step).
  onNext: (values: { firstName: string; lastName: string }) => void;
  // Name the user already typed earlier this session, if they've been here before.
  // Takes precedence over the server/Auth0 prefill so back-navigation shows edits.
  draft?: { firstName: string; lastName: string } | null;
  // Gates Continue until the org/EHR data needed to pick the next step has loaded
  // (so an EHR org isn't routed into the no-EHR flow before its type is known).
  continueDisabled?: boolean;
  // Primary action label — "Continue" mid-flow (BCBA), "Finish" when the profile
  // is the only step (non-BCBA providers). Defaults to OnboardingShell's "Continue".
  continueLabel?: string;
  // Progress stepper position. Omitted for the profile-only (single-step) flow so
  // the "Step X of Y" bar doesn't render.
  currentStep?: number;
  totalSteps?: number;
}

// Step 2 — "Let's get to know you". Confirms the inviting organization and
// collects the BCBA's profile basics. Prefilled from Auth0 + provider metadata
// (getLoginProvider) and the org-details query. Continue persists first/last
// name via the same updateProviderProfile mutation the portal profile page uses,
// then advances. Matches Figma frame 204:85.
export const GetToKnowYouStep: React.FC<GetToKnowYouStepProps> = ({
  onNext,
  draft,
  continueDisabled,
  continueLabel,
  currentStep,
  totalSteps,
}) => {
  const { user } = useAuth0();
  const { loginProviderDetailsQuery, orgnizationDetailsQuery } = useAdminData();

  const providerMeta = loginProviderDetailsQuery.data?.getLoginProvider?.providermetadata;
  const organizationName = orgnizationDetailsQuery.data?.getUserOrgnizationDetails?.name ?? "";

  const updateProviderProfile = useUpdateProviderProfileMutation({});

  const { RegisteredFormInput, formState, onSubmit, reset } = useFormUtils<OnboardingProfileForm>({
    // Show every blank required field's error at once (not native one-at-a-time).
    useNativeValidation: false,
    mutationFn: async (data) => {
      // Persist the BCBA's name to provider metadata (same mutation as the portal
      // profile page); the org + email fields are read-only and not submitted
      // (email can't be changed post-invite anyway).
      //
      // Per backend: a logged-in provider always has a providermetadata row, so
      // this update succeeds in normal use. The try/catch is a safety net for
      // malformed accounts (e.g. test users seeded without metadata, which
      // updateProviderProfile rejects with "Provider has no providermetadata") so
      // a save failure never traps the user mid-onboarding.
      try {
        await updateProviderProfile.mutateAsync({
          providerData: {
            firstName: data.firstName,
            lastName: data.lastName,
          },
        });
      } catch (err) {
        console.warn("updateProviderProfile failed during onboarding; advancing anyway", err);
      }
      onNext({ firstName: data.firstName, lastName: data.lastName });
    },
    defaultValues: {
      organization: organizationName,
      // Draft (what the user typed earlier this session) wins over the prefill so
      // back-navigation restores edits even if the server save didn't take.
      firstName: draft?.firstName ?? providerMeta?.firstName ?? user?.given_name ?? "",
      lastName: draft?.lastName ?? providerMeta?.lastName ?? user?.family_name ?? "",
      email: providerMeta?.email ?? user?.email ?? "",
    },
  });

  // defaultValues are captured once on mount, but the org/provider queries may
  // still be loading then. Re-seed the form when they resolve — but only while
  // the user hasn't started editing, so we never clobber in-progress input.
  const isDirty = formState.isDirty;
  React.useEffect(() => {
    if (isDirty) return;
    reset({
      organization: organizationName,
      firstName: draft?.firstName ?? providerMeta?.firstName ?? user?.given_name ?? "",
      lastName: draft?.lastName ?? providerMeta?.lastName ?? user?.family_name ?? "",
      email: providerMeta?.email ?? user?.email ?? "",
    });
  }, [
    organizationName,
    providerMeta?.firstName,
    providerMeta?.lastName,
    providerMeta?.email,
    user?.given_name,
    user?.family_name,
    user?.email,
    draft?.firstName,
    draft?.lastName,
  ]);

  return (
    <OnboardingShell
      onContinue={onSubmit}
      continueDisabled={continueDisabled}
      continueLabel={continueLabel}
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Headline size="medium">Let&apos;s get to know you</Headline>
      <Paragraph size="small" className="mt-2 text-text-secondary">
        Let us know some details about you to help make your account!
      </Paragraph>

      <div className="mt-6">
        <RegisteredFormInput
          formKey="organization"
          inputSize="full"
          formState={formState}
          label="Organization"
          disabled
        />
      </div>

      <div className="mt-6">
        <p className="text-base font-semibold text-text-primary">Your Profile Details</p>
        <Small className="mt-1 block text-text-tertiary">
          Tell us how you&apos;d like to be addressed. Your name will appear this way across Frontera.
        </Small>
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RegisteredFormInput formKey="firstName" inputSize="full" formState={formState} label="First Name" />
            <RegisteredFormInput formKey="lastName" inputSize="full" formState={formState} label="Last Name" />
          </div>
          <div>
            <RegisteredFormInput formKey="email" inputSize="full" formState={formState} label="Email" disabled />
            <Small className="mt-1.5 block text-text-tertiary">
              Seeing the wrong email or organization?{" "}
              <a
                href="https://share.hsforms.com/1pr1XKot_Ty2ajsdLGR6i5At9vgj"
                target="_blank"
                rel="noopener noreferrer"
                className="text-interactive-primary-default underline"
              >
                Contact Frontera support
              </a>
              {"."}
            </Small>
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
};
